import { useAtomValue } from "jotai";
import { useSearchParams } from "next/navigation";
import type { Dispatch, FunctionComponent, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { scroller } from "react-scroll";

import { accountAtom } from "shared/lib/account";

import { extractAfterPrefix } from "scmp-app/components/article/article-render/posties/helpers";
import type { Question, QuizStatus, ReviewQuestion } from "scmp-app/components/posties/quiz/types";

import { getQuizData } from "./helpers";
type ContextValue = {
  currentIndex: number;
  formId: null | string;
  handleNext?: () => void;
  questions: Question[];
  quizReference: React.MutableRefObject<HTMLDivElement | null>;
  quizStatus: QuizStatus;
  reviewQuestions: ReviewQuestion[];
  setCurrentIndex?: Dispatch<SetStateAction<number>>;
  setQuizStatus?: Dispatch<SetStateAction<QuizStatus>>;
  setReviewQuestions?: Dispatch<SetStateAction<ReviewQuestion[]>>;
};

type Props = {
  children: ReactNode;
  quizId?: string;
};

const PostiesQuizContext = createContext<ContextValue>({
  currentIndex: 0,
  formId: null,
  questions: [],
  quizReference: { current: null },
  quizStatus: "NOT_STARTED",
  reviewQuestions: [],
});

export const PostiesQuizContextProvider: FunctionComponent<Props> = ({ children, quizId }) => {
  const { user } = useAtomValue(accountAtom);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [quizStatus, setQuizStatus] = useState<QuizStatus>("NOT_STARTED");
  const [isShowQuiz, setIsShowQuiz] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [formId, setFormId] = useState<null | string>(null);
  const [reviewQuestions, setReviewQuestions] = useState<ReviewQuestion[]>([]);
  const quizReference = useRef<HTMLDivElement | null>(null);
  const searchParameters = useSearchParams();

  const handleNext = () => {
    if (currentIndex + 1 === questions.length) {
      setQuizStatus("FINISHED");
    } else {
      setCurrentIndex(previous => previous + 1);
    }
  };

  useEffect(() => {
    const form = extractAfterPrefix(window.location.href, ["quiz-id"]);

    const currentQuizId = quizId ?? form?.documentId;

    if (currentQuizId) {
      try {
        void (async () => {
          const quizData = await getQuizData({
            documentId: currentQuizId,
            email: user?.useremail ?? "",
          });

          if (quizData && quizData.form.questions.length > 0) {
            if (quizData.submissionStatus.isFinished) {
              setQuizStatus("FINISHED");
              setIsShowQuiz(true);
              setQuestions(quizData.form.questions);
              setReviewQuestions(quizData.submissionStatus.questions);
            } else {
              if (quizData.submissionStatus.questions.length > 0) {
                setCurrentIndex(quizData.submissionStatus.questions.length);
                setQuizStatus("RESUME");
              }
              setQuestions(quizData.form.questions);
              setFormId(currentQuizId);
              setIsShowQuiz(true);
            }
          }
        })();
      } catch {}
    }
  }, [quizId, user?.useremail]);

  useEffect(() => {
    const scrollToQuiz = searchParameters.get("scroll-to");

    if (quizReference.current && isShowQuiz && scrollToQuiz === "quiz") {
      const quizLabelHeight = 35;
      const postiesHeaderHeight = 100;

      setTimeout(() => {
        scroller.scrollTo("student-quiz", {
          duration: 800,
          offset: -postiesHeaderHeight - quizLabelHeight,
          smooth: true,
        });
      }, 1000);
    }

    return;
  }, [isShowQuiz, searchParameters]);

  if (!isShowQuiz) return null;

  return (
    <PostiesQuizContext.Provider
      value={{
        currentIndex,
        formId,
        handleNext,
        questions,
        quizReference,
        quizStatus,
        reviewQuestions,
        setCurrentIndex,
        setQuizStatus,
        setReviewQuestions,
      }}
    >
      {children}
    </PostiesQuizContext.Provider>
  );
};

export const usePostiesQuizContext = () => useContext(PostiesQuizContext);
